import React, { useMemo } from "react";
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { TimeseriesDayPoint } from "@/api/campaign";
import { useMaxWidth } from "@/shared/hooks/useMaxWidth";
import { PHONE_MAX_WIDTH } from "@/shared/theme/breakpoints";
import { dropLeadingZeroDays, formatMetricTick, metricScaleMinimum } from "../../utils/timeseriesChart";
import { ENGAGEMENTS_COLOR, VIEWS_COLOR, formatDayMonth, lineDataset } from "./chartTheme";
import { ChartArea, TimeseriesChartFrame, type ChartPlatform } from "./TimeseriesChartFrame";
import { WeeklyTimeseriesChart } from "./WeeklyTimeseriesChart";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

interface TimeseriesChartProps {
	series: TimeseriesDayPoint[];
	observationDays?: string[];
	timezone?: string;
	platform?: ChartPlatform;
	loading?: boolean;
}

const TimeseriesChart: React.FC<TimeseriesChartProps> = ({
	series,
	timezone = "UTC",
	platform = "all",
	loading = false,
}) => {
	const isPhone = useMaxWidth(PHONE_MAX_WIDTH);
	const visible = useMemo(
		() => dropLeadingZeroDays([...series].sort((left, right) => left.date.localeCompare(right.date))),
		[series],
	);

	// Phones chart weekly totals with a day-by-day card underneath (WeeklyTimeseriesChart).
	if (isPhone) {
		return <WeeklyTimeseriesChart series={visible} platform={platform} timezone={timezone} loading={loading} />;
	}

	const views = visible.map((point) => point.views);
	const engagements = visible.map((point) => point.engagements);
	const labels = visible.map((point) => formatDayMonth(point.date));

	const formatTicks = (value: unknown, _index: unknown, ticks: Array<{ value: number }>) =>
		formatMetricTick(
			Number(value),
			ticks.map((tick) => tick.value),
		);

	const line = { pointRadius: 0, showLine: true, spanGaps: false };
	const data = {
		labels,
		datasets: [
			{ ...lineDataset("Views", views, VIEWS_COLOR, "y"), ...line },
			{ ...lineDataset("Engagements", engagements, ENGAGEMENTS_COLOR, "y1"), ...line },
		],
	};

	const options = {
		responsive: true,
		maintainAspectRatio: false,
		layout: { padding: { top: 0 } },
		interaction: { mode: "index" as const, intersect: false },
		plugins: {
			legend: {
				position: "top" as const,
				align: "end" as const,
				labels: {
					usePointStyle: true,
					pointStyle: "circle",
					font: { family: "Poppins", size: 12 },
					padding: 30,
					boxWidth: 8,
					boxHeight: 8,
				},
				title: { display: true, padding: 0 },
			},
		},
		scales: {
			y: {
				type: "linear" as const,
				position: "left" as const,
				beginAtZero: false,
				min: metricScaleMinimum(views),
				grace: "10%",
				ticks: {
					callback: formatTicks,
					font: { family: "Poppins", size: 11 },
					color: VIEWS_COLOR,
				},
				grid: { color: "#f0f0f0" },
			},
			y1: {
				type: "linear" as const,
				position: "right" as const,
				beginAtZero: false,
				min: metricScaleMinimum(engagements),
				grace: "10%",
				ticks: {
					callback: formatTicks,
					font: { family: "Poppins", size: 11 },
					color: ENGAGEMENTS_COLOR,
				},
				grid: { drawOnChartArea: false },
			},
			x: {
				ticks: { font: { family: "Poppins", size: 11 }, color: "#969696" },
				grid: { display: false },
			},
		},
	};

	return (
		<TimeseriesChartFrame
			platform={platform}
			note="Observed growth days only"
			timezone={timezone}
			loading={loading}
			hasData={series.length > 0}
		>
			<ChartArea>
				<Line data={data} options={options} />
			</ChartArea>
		</TimeseriesChartFrame>
	);
};

export default TimeseriesChart;
